import { useEffect } from "react";
import { SpotifySessionApiError } from "../api";
import { useTokenContext } from "../auth/UserTokenProvider";

const NETWORK_RETRY_DELAYS_MS = [5000, 15000, 60000];

const getEnvMinutes = (key: string, fallbackMinutes: number) => {
  const rawValue = process.env[key];
  if (!rawValue) {
    return fallbackMinutes;
  }
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallbackMinutes;
  }
  return parsed;
};

const isRetryableRefreshError = (error: unknown): boolean =>
  !(error instanceof SpotifySessionApiError) || error.status >= 500;

const useTokenRefresh = () => {
  const { token, refreshToken } = useTokenContext();

  useEffect(() => {
    if (token === null) {
      return;
    }

    let cancelled = false;
    let retryIndex = 0;
    let timeoutId: number | null = null;
    let activeAttempt: Promise<void> | null = null;

    const clearScheduledRefresh = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const scheduleRefresh = (delayMs: number) => {
      clearScheduledRefresh();
      timeoutId = window.setTimeout(() => {
        timeoutId = null;
        void attemptRefresh();
      }, delayMs);
    };

    const scheduleRetry = (error: unknown) => {
      if (
        cancelled ||
        !isRetryableRefreshError(error) ||
        retryIndex >= NETWORK_RETRY_DELAYS_MS.length
      ) {
        return;
      }

      const retryDelay = NETWORK_RETRY_DELAYS_MS[retryIndex];
      retryIndex += 1;
      console.warn(`Token refresh failed; retrying in ${retryDelay / 1000} seconds`, error);
      scheduleRefresh(retryDelay);
    };

    function attemptRefresh(): Promise<void> {
      if (activeAttempt) {
        return activeAttempt;
      }

      activeAttempt = (async () => {
        try {
          const refreshed = await refreshToken();
          if (!cancelled && refreshed) {
            console.log("Token refreshed successfully");
          }
        } catch (error) {
          if (!cancelled) {
            console.error("Token refresh failed:", error);
            scheduleRetry(error);
          }
        }
      })().finally(() => {
        activeAttempt = null;
      });

      return activeAttempt;
    }

    const now = Date.now();
    const expiryTime = token.expiry.getTime();
    const refreshBufferMinutes = getEnvMinutes("REACT_APP_TOKEN_REFRESH_BUFFER_MINUTES", 5);
    const refreshBuffer = refreshBufferMinutes * 60 * 1000;
    const timeUntilRefresh = Math.max(0, expiryTime - now - refreshBuffer);

    console.log(`Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000)} seconds`);
    scheduleRefresh(timeUntilRefresh);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }

      const visibilityRefreshMinutes = getEnvMinutes(
        "REACT_APP_TOKEN_REFRESH_VISIBILITY_MINUTES",
        10
      );
      if (token.expiry.getTime() - Date.now() < visibilityRefreshMinutes * 60 * 1000) {
        console.log("Page became visible, refreshing token...");
        clearScheduledRefresh();
        void attemptRefresh();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      cancelled = true;
      clearScheduledRefresh();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token, refreshToken]);

  return null;
};

export { useTokenRefresh };
